import React, { useMemo } from 'react';
import { Controller, UseControllerProps, useFormContext } from 'react-hook-form';
import {
    getUseOrganizationByIdQueryKey,
    useFilterOrganizations,
    useOrganization,
} from '@/clients/organization/useOrganization.ts';
import { FilterOptionsOrganization, Organization } from '@/clients/organization/types/organization.ts';
import { useQueryClient } from '@tanstack/react-query';
import { SearchableCombobox, SearchableComboboxOption } from '@/shared/ui/Combobox/SearchableCombobox.tsx';
import { withDefaultSearchableComboboxOption } from '@/shared/ui/Combobox/utils/withDefaultSearchableComboboxOption.ts';

import { useFormFieldSearch } from '@/shared/hooks/useFormFieldSearch.ts';

type OrganizationFieldValues = {
    [key: string]: number | string | null;
};

export type SelectOrganizationInputProps = Pick<
    UseControllerProps<OrganizationFieldValues, string>,
    'name' | 'defaultValue'
> & {
    label?: string;
    placeholder?: string;
    filters?: Omit<FilterOptionsOrganization, 'search'>;
    dataTest?: string;
    exclude?: {
        types?: NonNullable<FilterOptionsOrganization['organizationType']>[];
        ids?: number[];
    };
    disabled?: boolean;
    className?: string;
    defaultValueLabel?: string;
    specialOptions?: SearchableComboboxOption[];
    onValueChange?: (org: Organization | undefined) => void;
};

export const SelectOrganizationInput = React.forwardRef<HTMLInputElement, SelectOrganizationInputProps>(
    (
        {
            name,
            defaultValue,
            defaultValueLabel,
            placeholder,
            label,
            filters,
            exclude,
            disabled,
            className,
            onValueChange,
            dataTest,
            specialOptions,
        },
        _ref
    ) => {
        const queryClient = useQueryClient();

        const { control, watch } = useFormContext();
        const { search, setSearch, debouncedSearch } = useFormFieldSearch(name);

        const currentValue = watch(name);
        const { data: currentOrganization } = useOrganization(typeof currentValue === 'number' ? currentValue : null);

        const { data, isFetching } = useFilterOrganizations({
            isActive: true,
            isBlocked: false,
            status: ['approved'],
            ...filters,
            search: debouncedSearch || undefined,
        });

        const organizations = useMemo(() => {
            const excludedIds = exclude?.ids ?? [];
            const excludedTypes: string[] = ['platform', ...(exclude?.types ?? [])];

            const filteredOrganizations = (data?.items ?? []).filter(
                org => !excludedIds.includes(org.id) && !org.types.some(type => excludedTypes.includes(type))
            );

            if (currentOrganization && !filteredOrganizations.some(org => org.id === currentOrganization.id)) {
                return [currentOrganization, ...filteredOrganizations];
            }

            return filteredOrganizations;
        }, [data, exclude, currentOrganization]);

        const organizationsOptions = useMemo(() => {
            const defaultOption =
                defaultValue && defaultValueLabel
                    ? {
                          value: String(defaultValue),
                          label: defaultValueLabel,
                      }
                    : undefined;

            return withDefaultSearchableComboboxOption({
                options: organizations.map(org => ({
                    value: String(org.id),
                    label: org.name,
                    description: `${org.billingAddress.street} ${org.billingAddress.number}, ${org.billingAddress.zip} ${org.billingAddress.city}`,
                })),
                existingOptions: specialOptions,
                defaultOption,
            });
        }, [organizations, defaultValue, defaultValueLabel, specialOptions]);

        const options = useMemo(
            () => [...(specialOptions ?? []), ...organizationsOptions],
            [specialOptions, organizationsOptions]
        );

        return (
            <Controller
                name={name}
                defaultValue={defaultValue ?? null}
                control={control}
                render={({ field: { ref, onChange, value }, fieldState }) => (
                    <SearchableCombobox
                        fieldRef={ref}
                        label={label}
                        placeholder={placeholder}
                        error={fieldState.error?.message}
                        search={search}
                        onSearchChange={setSearch}
                        loading={isFetching}
                        options={options}
                        value={value != null ? String(value) : undefined}
                        dataTest={dataTest}
                        className={className}
                        onValueChange={selectedValue => {
                            const selectedOrg = organizations.find(o => String(o.id) === selectedValue);

                            if (selectedOrg) {
                                queryClient.setQueryData(getUseOrganizationByIdQueryKey(selectedOrg.id), selectedOrg);
                            }

                            onValueChange?.(selectedOrg);
                            onChange(selectedOrg != null ? selectedOrg.id : (selectedValue ?? null));
                        }}
                        disabled={disabled}
                    />
                )}
            />
        );
    }
);
